import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { isSameFocus, parseFocusFromPath, type MirandaFocus } from '@/lib/miranda-focus';
import {
  DEFAULT_AGENT_ID,
  mirandaAgentKey,
  mirandaSessionKey,
  readMirandaStored,
  reconcileStoredAgent,
  writeMirandaStored,
} from '@/lib/miranda-storage';
import { agentApi } from '@/services/api';

// Types
export type PageType = 
  | 'dashboard' 
  | 'calendar' 
  | 'calendar-v2' 
  | 'reserves' 
  | 'clients' 
  | 'viatgers' 
  | 'persons'
  | 'mossos'
  | 'forms'
  | 'settings'
  | 'chat'
  | 'other';

export interface MirandaMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  toolCalls?: Array<{
    name: string;
    args: Record<string, unknown>;
    result?: unknown;
    state?: 'pending' | 'result' | 'approval_requested';
  }>;
  createdAt: Date;
}

export interface MirandaContextValue {
  // UI State
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;

  // Session
  sessionId: string | null;
  setSessionId: (id: string | null) => void;
  agentId: string;
  setAgentId: (id: string) => void;
  /**
   * Canvia cada cop que comença una conversa nova (Netejar, un altre agent, un altre usuari). El
   * xat ric del giny en fa la `key`: tornar-lo a muntar és l'única manera de buidar `useChat`.
   * Rebre la sessió d'una conversa nova (`setSessionId`) no el canvia.
   */
  conversationKey: number;

  // Messages
  messages: MirandaMessage[];
  setMessages: React.Dispatch<React.SetStateAction<MirandaMessage[]>>;
  isStreaming: boolean;
  setIsStreaming: (streaming: boolean) => void;

  // Context
  currentPage: PageType;
  /** El que l'usuari mira, derivat de l'URL. `null` fora de `/reserves/:ids`. */
  routeFocus: MirandaFocus | null;
  /** El que s'enviarà de debò: `routeFocus`, o `null` si l'usuari l'ha descartat. */
  focus: MirandaFocus | null;
  /** L'usuari ha clicat el xip per preguntar sense context. */
  focusDismissed: boolean;
  /** Descarta o recupera el context d'aquesta ruta. */
  setFocusDismissed: (dismissed: boolean) => void;

  // Tokens (comptador informatiu de la sessió, sense pressupost)
  tokensUsed: number;
  updateTokenUsage: (used: number) => void;

  // Actions
  clearSession: () => void;
}

const MirandaContext = createContext<MirandaContextValue | undefined>(undefined);

// Map routes to page types
function getPageTypeFromPath(pathname: string): PageType {
  if (pathname.startsWith('/dashboard')) return 'dashboard';
  if (pathname.startsWith('/calendar-v2')) return 'calendar-v2';
  if (pathname.startsWith('/calendar')) return 'calendar';
  if (pathname.startsWith('/reserves')) return 'reserves';
  if (pathname.startsWith('/clients')) return 'clients';
  if (pathname.startsWith('/viatgers')) return 'viatgers';
  if (pathname.startsWith('/persons')) return 'persons';
  if (pathname.startsWith('/mossos')) return 'mossos';
  if (pathname.startsWith('/forms')) return 'forms';
  if (pathname.startsWith('/admin/settings')) return 'settings';
  if (pathname.startsWith('/chat')) return 'chat';
  return 'other';
}

interface MirandaProviderProps {
  children: ReactNode;
}

/**
 * Estat global de Miranda.
 *
 * El proveïdor viu per sobre de `<Routes>` (`App.tsx`): la conversa sobreviu a la navegació
 * a propòsit. Només es reinicia amb "Netejar" o en canviar d'agent — canviar de pàgina no
 * hi toca. El nivell de context, el pressupost de tokens i `pageData` s'han tret: no
 * arribaven enlloc (el backend no els llegia) i el pressupost era un número inventat.
 *
 * El context real de la pregunta és el `focus`, **derivat de l'URL** (F2): amb
 * `/reserves/92725627` obert, tot missatge porta `{ type: 'reservation', beds24Ids: [...] }`
 * i el backend en munta el bloc. Del navegador només en surten ids.
 */
export const MirandaProvider: React.FC<MirandaProviderProps> = ({ children }) => {
  const location = useLocation();
  const currentPage = getPageTypeFromPath(location.pathname);

  // 🆕 F2 — El focus es **deriva** de l'URL a cada render. No hi ha res a mantenir a mà:
  // aquest és exactament l'error de `pageData`, que esperava que cada pàgina l'omplís i
  // per això sempre va estar buit.
  const routeFocus = React.useMemo(
    () => parseFocusFromPath(location.pathname, location.search),
    [location.pathname, location.search]
  );

  // "Pregunta sense context": el xip del panell descarta el focus d'aquesta ruta. Es
  // recupera sol en canviar de reserva, perquè descartar-lo és per a una pregunta, no per
  // a sempre.
  const [focusDismissed, setFocusDismissed] = useState(false);
  const dismissedForRef = useRef<typeof routeFocus>(null);
  useEffect(() => {
    if (isSameFocus(dismissedForRef.current, routeFocus)) return;
    dismissedForRef.current = routeFocus;
    setFocusDismissed(false);
  }, [routeFocus]);

  const focus = focusDismissed ? null : routeFocus;

  // UI State
  const [isOpen, setIsOpen] = useState(false);

  // Qui pregunta. La sessió desada és **d'aquesta persona**: al PC del mostrador hi entren
  // en Pere i la Carme, i el backend respon 404 si el `sessionId` és d'un altre (revisió M1).
  const { user } = useAuth();
  const userId = user?.id ?? null;

  // Session (persistida: obrir un enllaç a /reserves/:ids és una càrrega completa)
  const [sessionId, setSessionIdState] = useState<string | null>(null);
  const [agentId, setAgentIdState] = useState<string>(DEFAULT_AGENT_ID);
  const [conversationKey, setConversationKey] = useState(0);

  // Messages
  const [messages, setMessages] = useState<MirandaMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);

  // Tokens
  const [tokensUsed, setTokensUsed] = useState(0);

  // `checkAuth()` és asíncron: en el primer render encara no se sap qui és. Es llegeix
  // l'emmagatzematge quan arriba l'usuari, i es buida quan canvia o quan tanca la sessió.
  const hydratedForRef = useRef<string | null>(null);
  // El mateix, com a estat: la migració de l'agent (més avall) només pot llegir `agentId` i
  // `sessionId` quan ja són els d'aquesta persona, no els del render d'abans.
  const [hydratedUserId, setHydratedUserId] = useState<string | null>(null);
  useEffect(() => {
    if (hydratedForRef.current === userId) return;
    hydratedForRef.current = userId;

    setMessages([]);
    setTokensUsed(0);
    setConversationKey((key) => key + 1);
    setHydratedUserId(userId);

    if (!userId) {
      setSessionIdState(null);
      setAgentIdState(DEFAULT_AGENT_ID);
      return;
    }

    setSessionIdState(readMirandaStored(mirandaSessionKey(userId)));
    setAgentIdState(readMirandaStored(mirandaAgentKey(userId)) || DEFAULT_AGENT_ID);
  }, [userId]);

  // Els agents que l'usuari pot fer servir: la mateixa clau que el selector del giny i de `/chat`,
  // així que és una sola crida. Diu si la Miranda única hi és o si el fre és posat.
  const { data: availableAgents } = useQuery({
    queryKey: ['agents'],
    queryFn: agentApi.getAgents,
    enabled: !!userId,
  });

  const setSessionId = useCallback(
    (id: string | null) => {
      setSessionIdState(id);
      if (userId) writeMirandaStored(mirandaSessionKey(userId), id);
    },
    [userId]
  );

  // Actions
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  const updateTokenUsage = useCallback((used: number) => {
    setTokensUsed((prev) => prev + used);
  }, []);

  const clearSession = useCallback(() => {
    setSessionId(null);
    setMessages([]);
    setTokensUsed(0);
    setConversationKey((key) => key + 1);
  }, [setSessionId]);

  // Canviar d'agent obre conversa nova: la sessió del backend guarda l'agent que la va
  // començar (routing "sticky"), i continuar-la amb un altre barrejaria dos contextos.
  const setAgentId = useCallback(
    (id: string) => {
      if (id === agentId) return;
      setAgentIdState(id);
      if (userId) writeMirandaStored(mirandaAgentKey(userId), id);
      clearSession();
    },
    [agentId, clearSession, userId]
  );

  // La Miranda única per defecte (en Pere, 14-OCT-2026), quan arriba la llista d'agents i mai
  // abans (`lib/miranda-agents.ts`, `nextStoredAgentStep`): qui tenia desat un agent vell passa a
  // `miranda` una sola vegada; si té una conversa d'aquell agent, la continua amb el client de text
  // i el canvi arriba amb «Netejar» (aquest efecte torna a córrer quan `sessionId` queda buit). Amb
  // el fre (`miranda` fora de la llista), qui era a `miranda` passa a `router`.
  useEffect(() => {
    if (!userId || hydratedUserId !== userId) return;
    const next = reconcileStoredAgent(userId, { agentId, hasSession: !!sessionId }, availableAgents);
    if (next === null) return;
    setAgentIdState(next);
    clearSession();
  }, [userId, hydratedUserId, agentId, sessionId, availableAgents, clearSession]);

  const value: MirandaContextValue = {
    // UI State
    isOpen,
    open,
    close,
    toggle,

    // Session
    sessionId,
    setSessionId,
    agentId,
    setAgentId,
    conversationKey,

    // Messages
    messages,
    setMessages,
    isStreaming,
    setIsStreaming,

    // Context
    currentPage,
    routeFocus,
    focus,
    focusDismissed,
    setFocusDismissed,

    // Tokens
    tokensUsed,
    updateTokenUsage,

    // Actions
    clearSession,
  };

  return (
    <MirandaContext.Provider value={value}>{children}</MirandaContext.Provider>
  );
};

export const useMiranda = (): MirandaContextValue => {
  const context = useContext(MirandaContext);
  if (!context) {
    throw new Error('useMiranda must be used within a MirandaProvider');
  }
  return context;
};

export default MirandaContext;
